import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  getDurableFacilitatedSession,
} from "../services/durableFacilitatedSessionApi";
import SessionResponseDisplay from "../../../activities/live-sessions/session-views/SessionResponseDisplay";

function formatDate(value) {
  if (!value) {
    return "Unknown";
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? "Unknown"
    : date.toLocaleString();
}

function renderPerceptualPayload(response) {
  if (response?.payload?.responseRepresentation === "normalized") {
    return (
      <SessionResponseDisplay
        response={response.payload}
        role={response.responseRole || response.payload.responseRole || "participant"}
      />
    );
  }
  const perceptualData =
    response?.payload?.perceptualData || {};
  const entries =
    Object.entries(perceptualData);

  if (entries.length === 0) {
    return <p>No normalized response payload available.</p>;
  }

  return (
    <ul>
      {entries.map(([key, value]) => (
        <li key={key}>
          <strong>
            {value?.attributeName || key}
          </strong>
          {value?.VASValue !== undefined && (
            <> — VAS: {value.VASValue}</>
          )}
          {value?.attributeComment && (
            <> — {value.attributeComment}</>
          )}
        </li>
      ))}
    </ul>
  );
}

function WarningList({ warnings = [] }) {
  if (!warnings.length) {
    return <p>No provenance warnings.</p>;
  }

  return (
    <ul>
      {warnings.map((warning, index) => (
        <li key={`${warning.code}-${index}`}>
          <strong>{warning.code}</strong>
          {warning.path ? ` (${warning.path})` : ""}:{" "}
          {warning.message}
        </li>
      ))}
    </ul>
  );
}

export default function DurableFacilitatedSessionDetail() {
  const { durableSessionId } = useParams();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    async function loadSession() {
      try {
        setLoading(true);
        const loadedSession =
          await getDurableFacilitatedSession(durableSessionId);

        if (active) {
          setSession(loadedSession);
          setError("");
        }
      } catch (loadError) {
        if (active) {
          setError(
            loadError?.response?.status === 404
              ? "Captured session not found."
              : "Captured session could not be loaded."
          );
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadSession();

    return () => {
      active = false;
    };
  }, [durableSessionId]);

  if (loading) {
    return (
      <div className="app-section">
        <h1>Captured session review</h1>
        <p>Loading captured session...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app-section">
        <h1>Captured session review</h1>
        <p className="warning-message">{error}</p>
        <Link to="/app/durable-facilitated-sessions">
          Back to captured sessions
        </Link>
      </div>
    );
  }

  if (!session) {
    return null;
  }

  const allWarnings = [
    ...(session.warnings || []),
    ...(session.stimulus?.warnings || []),
    ...(session.aggregate?.warnings || []),
  ];

  return (
    <div className="app-section">
      <Link to="/app/durable-facilitated-sessions">
        Back to captured sessions
      </Link>

      <h1>Captured session review</h1>

      <section>
        <h2>Metadata</h2>
        <dl>
          <dt>Captured</dt>
          <dd>{formatDate(session.capturedAt)}</dd>
          <dt>Status</dt>
          <dd>{session.captureStatus}</dd>
          <dt>Activity</dt>
          <dd>
            {session.activity?.compatibilityToolName ||
              session.activity?.normalizedActivityType}
          </dd>
          <dt>Semantic activity</dt>
          <dd>{session.activity?.semanticActivityIdentity}</dd>
          <dt>Source runtime</dt>
          <dd>
            {session.sourceRuntime?.type} /{" "}
            {session.sourceRuntime?.id}
          </dd>
          <dt>Original session code</dt>
          <dd>{session.sourceRuntime?.code || "Not available"}</dd>
          <dt>Source expires</dt>
          <dd>{formatDate(session.sourceRuntime?.expiresAt)}</dd>
        </dl>
      </section>

      <section>
        <h2>Stimulus</h2>
        <p>
          {session.stimulus?.sampleName ||
            session.stimulus?.selectedSampleId ||
            "Unknown sample"}
        </p>
        {session.stimulus?.sampleLink ? (
          <audio controls src={`/${session.stimulus.sampleLink}`}>
            Your browser does not support the audio element.
          </audio>
        ) : (
          <p className="warning-message">
            Sample audio is unavailable for this durable review.
          </p>
        )}
      </section>

      <section>
        <h2>Participant roster</h2>
        {session.participants?.length ? (
          <ul>
            {session.participants.map((participant) => (
              <li key={participant.participantRef}>
                {participant.displayLabel ||
                  participant.participantRef}{" "}
                <span>({participant.identityMode})</span>
              </li>
            ))}
          </ul>
        ) : (
          <p>No participant roster was captured.</p>
        )}
      </section>

      <section>
        <h2>Normalized responses</h2>
        {session.responses?.length ? (
          session.responses.map((response) => (
            <article
              key={response.responseRef}
              className="app-card"
            >
              <h3>
                {response.participantName ||
                  response.participantRef}
              </h3>
              <p>Status: {response.validationStatus}</p>
              <p>Role: {response.responseRole || "participant"}</p>
              {renderPerceptualPayload(response)}
              {response.warnings?.length > 0 && (
                <>
                  <h4>Response warnings</h4>
                  <WarningList warnings={response.warnings} />
                </>
              )}
            </article>
          ))
        ) : (
          <p>No responses were captured.</p>
        )}
      </section>

      <section>
        <h2>Derived aggregate snapshot</h2>
        <p>This derived reference is not a participant response or facilitator consensus.</p>
        <p>
          Algorithm: {session.aggregate?.algorithmVersion || "Unknown"}
        </p>
        <p>Status: {session.aggregate?.status || "Unknown"}</p>
        {Object.keys(session.aggregate?.payload || {}).length > 0 ? (
          <pre>
            {JSON.stringify(session.aggregate.payload, null, 2)}
          </pre>
        ) : (
          <p>No aggregate snapshot is available.</p>
        )}
      </section>

      <section>
        <h2>Provenance warnings</h2>
        <WarningList warnings={allWarnings} />
      </section>
    </div>
  );
}
